import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { createBlock } from "@/api/blocks";
import { isApiError } from "@/api/errors";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { trackFeature } from "@/telemetry";
import { BlockForm, type BlockFormValues } from "../components/BlockForm";

export function BlockCreatePage(): JSX.Element {
  const { farmId = "" } = useParams<{ farmId: string }>();
  const { t } = useTranslation("farms");
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: BlockFormValues): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const block = await createBlock(farmId, values);
      trackFeature("block_create", { farm_id: farmId, props: { action: "created" } });
      navigate(`/farms/${farmId}/blocks/${block.id}`);
    } catch (err) {
      setError(isApiError(err) ? (err.problem.detail ?? err.problem.title) : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      {/* Single-column forms cap their own measure: a 1152px-wide field row
          puts unreadable distance between a label and its input. The page
          frame stays wide so the header lines up with every other page. */}
      <div className="flex max-w-3xl flex-col gap-4">
        <PageHeader
          above={
            <Breadcrumb
              items={[
                { label: t("list.heading"), to: "/farms" },
                { label: t("block.back"), to: `/farms/${farmId}` },
                { label: t("detail.addBlock") },
              ]}
            />
          }
          title={t("detail.addBlock")}
        />
        <BlockForm
          submitLabel={t("form.submitBlockCreate")}
          onSubmit={handleSubmit}
          onCancel={() => navigate(`/farms/${farmId}`)}
          busy={busy}
          error={error}
        />
      </div>
    </Page>
  );
}
